import { SearchResultsType } from "../components/shared";
import { superCarteiraFeatures } from "../pages";
import { generatePortfolioName } from "../utils";

export interface BenchmarkAssetType {
  assetType: string;
  identifier: number;
  label: string;
}
export interface BenchmarkData {
  asset: BenchmarkAssetType;
}
export interface BenchmarksType {
  [key: string]: BenchmarkData;
}

export interface PortfoliosType {
  [key: number]: PortfolioType;
}
export interface PortfolioType {
  assets: AssetsType;
  assetType: "PORTFOLIO" | undefined;
  identifier: string | undefined;
  name: string;
  totalAmount: number;
}

export interface AssetData {
  amount: number | null;
  asset: SearchResultsType;
  percentage: number | null;
  isLocked: boolean;
}
export interface AssetsType {
  [key: string]: AssetData;
}

export interface CurrenciesConfigsType {
  availableCurrencies: string[];
  currentCurrency: string;
}

export interface DateRangeType {
  initialDate: string | null;
  finalDate: string | null;
}

interface MinMaxValueType {
  min: number;
  max: number;
  default: number;
}

export type CompetenceType = string[];

export type PtBrFiltersType =
  | "ACAO"
  | ""
  | "RENDA_FIXA"
  | "PREVIDENCIA"
  | "FUNDO"
  | "EUROPA"
  | "VALORIZACAO"
  | "DIVERSIFICACAO"
  | "ESTABILIDADE"
  | "ANTIFRAGILIDADE"
  | "OUTROS"
  | "BENCHMARK_PERSONALIZADO";

export type TabsType =
  | "supercarteira"
  | "previdencia"
  | "portfolio"
  | "asset"
  | "importSupercarteira"
  | "importPrevidencia"
  | "monitoring"
  | "learnToUse";

export type PortfolioEditor = {
  isExpanded: boolean;
  isSuperExpanded: boolean;
  isModalOpen: boolean;
};

interface GlobalConfigsType {
  superCarteiraFeature: superCarteiraFeatures;
  orderedPortfolioCategories: PtBrFiltersType[];
  minCharsForSearch: number;
  currency: CurrenciesConfigsType;
  language: string;
  availableConsolidated: string[];
  availableCompetences: CompetenceType;
  maxAllowedPortfolios: number;
  portfolioBeingEdited: number | null;
  showAssets: boolean;
  showSideEditor: boolean;
  customDateRange: DateRangeType;
  fetchedDateRange: DateRangeType;
  minSelectableDate: Date;
  showShareError: boolean;
  showImportedNotifier: boolean;
  initialApplication: MinMaxValueType;
  windowValue: MinMaxValueType;
  defaultCustomBenchmarkAssetPercentage: number;
  maxCustomBenchmarkAssetPercentage: number;
  benchmarksInitialColor: number;
  portfolioEditor: PortfolioEditor;
}

export type LoadingFromSharedLinkType = {
  [key in "isLoadingPeriods" | "isLoadingCompetence"]: boolean;
};

export type UserDataType = {
  email: string;
  userId: string;
  portfolios: {
    supercarteira: {
      id: string | null;
      benchmark: (string | number)[] | null;
      competence: string | null;
      consolidated: string | null;
      currentPortfolioId: number | null;
      initialApplication: number | null;
      period: string | null;
      selectedCustomDateRange: [string, string] | null;
      windowValue: number | null;
    };
    previdencia: {
      id: string | null;
      benchmark: (string | number)[] | null;
      competence: string | null;
      consolidated: string | null;
      currentPortfolioId: number | null;
      initialApplication: number | null;
      period: string | null;
      selectedCustomDateRange: [string, string] | null;
      windowValue: number | null;
    };
  };
};

export interface ReduxStateType {
  isUserAuthenticated: boolean;
  applicationAmount: number;
  windowValue: number;
  benchmarks: BenchmarksType;
  searchableCustomBenchmarks: SearchResultsType[];
  showAllSearchableCustomBenchmarks: boolean;
  currentPortfolioId: number | null;
  sharedLinkTasks: LoadingFromSharedLinkType;
  globalConfigs: GlobalConfigsType;
  period:
    | "OTIMO"
    | "NO_MES"
    | "UM_ANO_UTIL"
    | "SEIS_MESES_UTEIS"
    | "CINCO_ANOS_UTEIS"
    | "DATA_PERSONALIZADA"
    | string;
  consolidated:
    | "ATIVOS"
    | "TIPO_DO_ATIVO"
    | "SETOR_DO_ATIVO"
    | "RISCO_DO_ATIVO"
    | "CLASSE_DO_ATIVO"
    | string;
  competence: string;
  portfolios: PortfoliosType;
  selectedTab: TabsType;
  spinner: boolean;
  userData: UserDataType;
}

export const initialState: ReduxStateType = {
  isUserAuthenticated: false,
  applicationAmount: 100,
  windowValue: 20,
  benchmarks: {},
  searchableCustomBenchmarks: [],
  showAllSearchableCustomBenchmarks: false,
  currentPortfolioId: null,
  sharedLinkTasks: {
    isLoadingPeriods: false,
    isLoadingCompetence: false,
  },
  globalConfigs: {
    superCarteiraFeature: "supercarteira",
    orderedPortfolioCategories: [
      "ESTABILIDADE",
      "DIVERSIFICACAO",
      "VALORIZACAO",
      "ANTIFRAGILIDADE",
      "OUTROS",
      "ACAO",
      "RENDA_FIXA",
      "PREVIDENCIA",
      "FUNDO",
      "BENCHMARK_PERSONALIZADO",
      "",
    ],
    minCharsForSearch: 3,
    currency: {
      currentCurrency: "BRL",
      availableCurrencies: ["BRL", "USD", "EUR"],
    },
    availableConsolidated: [
      "ATIVOS",
      "CLASSE_DO_ATIVO",
      "RISCO_DO_ATIVO",
      "SETOR_DO_ATIVO",
      "TIPO_DO_ATIVO",
    ],
    availableCompetences: [],
    language: "",
    maxAllowedPortfolios: 2,
    portfolioBeingEdited: null,
    showSideEditor: false,
    showAssets: false,
    customDateRange: { initialDate: null, finalDate: null },
    fetchedDateRange: { initialDate: null, finalDate: null },
    minSelectableDate: new Date("01-01-1970"),
    showShareError: false,
    showImportedNotifier: false,
    initialApplication: {
      min: 1,
      max: 1_000_000_000,
      default: 100,
    },
    windowValue: {
      min: 1,
      max: 1_000,
      default: 20,
    },
    defaultCustomBenchmarkAssetPercentage: 100,
    maxCustomBenchmarkAssetPercentage: 999.999,
    benchmarksInitialColor: 12,
    portfolioEditor: {
      isExpanded: false,
      isSuperExpanded: false,
      isModalOpen: false,
    },
  },
  period: "UM_ANO_UTIL",
  consolidated: "ATIVOS",
  competence: "",
  portfolios: {
    0: {
      assets: {},
      assetType: "PORTFOLIO",
      identifier: "",
      name: generatePortfolioName({}),
      totalAmount: 0,
    },
  },
  selectedTab: "portfolio",
  spinner: false,
  userData: {
    email: "",
    userId: "",
    portfolios: {
      supercarteira: {
        id: null,
        benchmark: null,
        competence: null,
        consolidated: null,
        currentPortfolioId: null,
        initialApplication: null,
        period: null,
        selectedCustomDateRange: null,
        windowValue: null,
      },
      previdencia: {
        id: null,
        benchmark: null,
        competence: null,
        consolidated: null,
        currentPortfolioId: null,
        initialApplication: null,
        period: null,
        selectedCustomDateRange: null,
        windowValue: null,
      },
    },
  },
};

export * from "./hooks";
export * from "./reducer";
